import RecruitApiClient from '../../RecruitApiClient';

import type {
  IExportParams,
  INewJobDefaults,
  INewJobParam,
  IRequestParams,
  JobListResponse,
  JobListStatusUpdate
} from './types';
import type { IJob } from '../Job/types';
import type { IEntity } from '../SmartForms/types';
import type { IJobTemplate } from '../Templates/types';

const BASE_URL = '/api/v4';

/* eslint-disable class-methods-use-this */
class Api {
  getJobs = async (params: IRequestParams) =>
    RecruitApiClient.get<JobListResponse>(`${BASE_URL}/jobs`, params);

  getJob = async (jobId: number) => RecruitApiClient.get(`${BASE_URL}/jobs/${jobId}`);

  getFilters = async (url: string, params: { 'filter[archived]': boolean }) =>
    RecruitApiClient.get(`${BASE_URL}/jobs/${url}`, params);

  getJobFilterOptionsPaginated = async (
    url: string,
    params: {
      'filter[archived]': boolean;
      limit?: number;
      page: number;
      search?: string;
      selected_id?: number;
    }
  ) => RecruitApiClient.get(`${BASE_URL}/jobs/${url}`, params);

  getUISettings = async (params) =>
    RecruitApiClient.get(`${BASE_URL}/ui_preferences`, params, {
      cache: 'no-cache'
    });

  postJobNote = async (jobId: number, params: { job_note: { body: string } }) =>
    RecruitApiClient.post(`${BASE_URL}/jobs/${jobId}/job_notes`, params);

  postUISettings = async (params: object) =>
    RecruitApiClient.post(`${BASE_URL}/ui_preferences`, params);

  putChangedState = async (link: string) => RecruitApiClient.put<JobListStatusUpdate>(link);

  putChangedStatus = async (jobId: number, statusID: number) =>
    RecruitApiClient.put<JobListStatusUpdate>(`${BASE_URL}/jobs/${jobId}/status/${statusID}`);

  putPinJob = async (jobId: number) => RecruitApiClient.put(`${BASE_URL}/jobs/${jobId}/pin`);

  putUnpinJob = async (jobId: number) => RecruitApiClient.put(`${BASE_URL}/jobs/${jobId}/unpin`);

  putArchiveJobs = async (params: object) =>
    RecruitApiClient.put(`${BASE_URL}/jobs/archive`, params);

  putUnrchiveJobs = async (params: object) =>
    RecruitApiClient.put(`${BASE_URL}/jobs/unarchive`, params);

  getCSVExport = async (params: IExportParams) =>
    RecruitApiClient.get(`${BASE_URL}/jobs/export`, params);

  referenceValidation = async (params: {
    'job[reference]': string;
  }): Promise<{ res: { valid: boolean; correct_format: boolean } }> =>
    RecruitApiClient.get(`${BASE_URL}/jobs/validating_reference`, params);

  referencePrefix = async (params) =>
    RecruitApiClient.get(`${BASE_URL}/jobs/auto_job_reference_setting`, params);

  getRequisitionJobDefaults = async (
    requisitionId: number
  ): Promise<{
    res: { job_defaults: Partial<Record<keyof INewJobDefaults, INewJobParam['value']>> };
  }> =>
    RecruitApiClient.get(`${BASE_URL}/jobs/requisition_job_defaults`, {
      requisition_id: requisitionId
    });

  importResources = async (jobId: number, params: { body: string }, queryParams: string) =>
    RecruitApiClient.post(`${BASE_URL}/jobs/${jobId}/import_resources${queryParams}`, params);

  cloneJobsOptions = async (params: object) =>
    RecruitApiClient.get(`${BASE_URL}/jobs/jobs_for_clone`, params);

  createJob = async (params: { body: string }) =>
    RecruitApiClient.post(`${BASE_URL}/jobs/`, params);

  getEntities = async (): Promise<{ res: { entities: IEntity[]; current_entity: IEntity } }> =>
    RecruitApiClient.get(`/api/v4/entities`);

  getJobTemplates = async (): Promise<{ res: { job_templates: IJobTemplate[] } }> =>
    RecruitApiClient.get(`${BASE_URL}/job_templates`, { override_limit: true, limit: 100 });

  getJobTemplate = async (jobTemplateId: number): Promise<{ res: IJob }> =>
    RecruitApiClient.get(`${BASE_URL}/job_templates/${jobTemplateId}`);

  getTargetEntities = async (): Promise<{ res: { entities: IEntity[] } }> =>
    RecruitApiClient.get(`${BASE_URL}/target_entities`);

  // Omitting entity_id answers for the user's own entity, which is the entity a new
  // job belongs to when the creator has no entity picker (UIUX-4001).
  getSeekAdSyncStatus = async (entityId?: number): Promise<{ res: { ad_sync_active: boolean } }> =>
    RecruitApiClient.get(
      `${BASE_URL}/seek/ad_sync_status`,
      entityId ? { entity_id: entityId } : {}
    );

  bulkMoveJobs = async (params: { job_ids: number[]; entity_id: number }) =>
    RecruitApiClient.post(`${BASE_URL}/jobs/bulk_move`, params);

  createJobFromTemplate = async (params: {
    reference: string;
    title: string;
    expires_at: string | null;
    location: string;
    requisition_ids: (number | undefined)[] | undefined;
    entity_id: number | undefined;
    job_template_id: number;
  }) => RecruitApiClient.post(`${BASE_URL}/jobs/import_template`, params);
}
/* eslint-enable class-methods-use-this */

const api = new Api();
export default api;
